import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { useSidebar } from '@neo/primitives/sidebar'
import { ReactComponent as VibeMascot } from '@shared/assets/icons/vibe-mascot.svg'
import { ChevronDown, Moon, PanelLeft, Search, Sun } from 'lucide-react'
import { REGIONS } from 'v2/configs/constants'
import { useCurrentStatusQuery } from 'v2/pages/settings/org-status/queries'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useDarkMode } from 'v2/shared/hooks/use-dark-mode'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { getShortcutModifierLabel } from 'v2/shared/utils/platform'
import { resolveAgentStatusColor } from 'v2/shared/utils/status-colors'
import { useAppStore } from 'v2/state/app'
import { getLocalAuth } from 'v2/utils'
import { HumanSpecialistPopover } from './human-specialist-popover'
import { getPlanLabel } from './plan-label'
import { StatusMenu } from './status-menu'
import { SupportSessionPill } from './support-session-pill'
import { UserAvatarPopover } from './user-avatar-popover'

const REGION_DISPLAY_MAP: Record<string, string> = {
  [REGIONS.US]: 'US',
  [REGIONS.IN]: 'India',
}

interface AppHeaderProps {
  onSearchClick: () => void
}

export const AppHeader = ({ onSearchClick }: AppHeaderProps) => {
  const { toggleSidebar } = useSidebar()
  const isAssistantOpen = useAppStore((s) => s.isAssistantOpen)
  const setIsAssistantOpen = useAppStore((s) => s.setIsAssistantOpen)
  const [isDarkMode, toggleDarkMode] = useDarkMode()

  const { data: profileData } = useProfileQuery()
  const { data: currentStatus } = useCurrentStatusQuery()
  const { plan, isTrial, trialDaysLeft, isTrialExpired, trialEndDate } = useUserPlan()

  const humanSpecialistEnabled = profileData?.organizations?.[0]?.human_specialist_enabled ?? false
  const region = getLocalAuth().region
  const regionLabel = REGION_DISPLAY_MAP[region] ?? region
  const planLabel = getPlanLabel(plan, { isTrial, trialDaysLeft, isTrialExpired, trialEndDate })

  return (
    <header className="flex items-center justify-between h-14 px-4 border-b bg-background">
      {/* Left side - Collapse button + info pills */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={toggleSidebar} data-testid="sidebar-toggle">
          <PanelLeft className="h-4 w-4" />
        </Button>
        {regionLabel && (
          <span
            className="hidden sm:inline text-s-400 text-secondary"
            data-testid="header-data-region"
          >
            Data Region: <span className="text-s-600 text-foreground">{regionLabel}</span>
          </span>
        )}
        {planLabel && (
          <span
            className="hidden sm:flex items-center h-6 px-2.5 rounded border border-st2 bg-bg2 dark:bg-hv1 font-inter text-primary text-xs-500 whitespace-nowrap"
            data-testid="header-plan-pill"
          >
            {planLabel}
          </span>
        )}
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        <SupportSessionPill />

        {/* Agent Status Dropdown */}
        {humanSpecialistEnabled && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="agent-status-dropdown"
              >
                <div
                  className="h-2 w-2 rounded-full"
                  style={{
                    background: currentStatus?.color
                      ? resolveAgentStatusColor(currentStatus.color)
                      : 'hsl(var(--status-success))',
                  }}
                />
                <span>{currentStatus?.status ?? 'Available'}</span>
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" align="start">
              <StatusMenu />
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {humanSpecialistEnabled && <HumanSpecialistPopover />}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSearchClick}
          className="hidden md:flex h-[30px] items-center gap-1.5 px-3 text-tertiary"
          data-testid="header-search-trigger"
        >
          <Search className="size-4 shrink-0" strokeWidth={1.5} />
          <span className="flex-1 text-left">Search</span>
          <span className="flex shrink-0 items-center gap-1 text-xxs-600">
            <span className="text-xs leading-none">{getShortcutModifierLabel()}</span>
            <span className="leading-none">K</span>
          </span>
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAssistantOpen(!isAssistantOpen)}
          data-testid="ask-buddy-btn"
          className="gap-1.5 items-center"
        >
          <VibeMascot className="h-5 w-5 -mt-0.5" />
          <span className="hidden sm:inline">Ask Buddy</span>
        </Button>

        <Button
          variant="ghost"
          size="iconSm"
          className="h-[30px] w-[30px]"
          onClick={toggleDarkMode}
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          data-testid="button-header-theme-toggle"
        >
          {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <UserAvatarPopover />
      </div>
    </header>
  )
}
